import { CloseOutlined } from '@ant-design/icons';
import { Flex, Table } from 'antd';
import { ReactNode } from 'react';
import { TFunction, useTranslation } from 'translations/hooks';

import { getTasksByCategory } from 'business/shift-report/report/pages/ReportView/services';
import {
  ShowMoreOnMobileState,
  ShowMoreOnMobileStateKeys,
  SHOW_MORE_ON_MOBILE_STATE_KEYS,
} from 'business/shift-report/report/pages/ReportView/types';
import { formatDurationToReadable } from 'business/shift-report/report/services/timeOperations';
import { GetShiftReportPdfMetadataQuery } from 'generated/graphql';
import { useMediaType } from 'technical/media/hooks';
import { ShowMoreToggle } from 'ui/show-more-button';
import { ShowMoreContainer } from 'ui/show-more-button/show-more-container';

import { MobileCardHeader, MobileTableHeader } from './mobile-header';

const COLUMN_WIDTH = 150;

const getTableColumnsWithDescription = (t: TFunction) => [
  {
    title: t('pages.print.table.startTime'),
    dataIndex: 'startTime',
    width: COLUMN_WIDTH,
  },
  {
    title: t('pages.print.table.endTime'),
    dataIndex: 'endTime',
    width: COLUMN_WIDTH,
  },
  {
    title: t('pages.print.table.spentTime'),
    dataIndex: 'spentTime',
    render: (node: number) => formatDurationToReadable(node),
    width: COLUMN_WIDTH,
  },
  {
    title: t('pages.print.table.ring'),
    dataIndex: 'ring',
    width: COLUMN_WIDTH,
  },
  {
    title: t('pages.print.table.critical'),
    dataIndex: 'critical',
    render: (check: boolean) => (check ? <CloseOutlined /> : null),
    width: COLUMN_WIDTH,
  },
  {
    title: t('pages.print.table.description'),
    dataIndex: 'description',
    width: COLUMN_WIDTH,
  },
  {
    title: t('pages.print.table.note'),
    dataIndex: 'note',
    width: COLUMN_WIDTH,
  },
];

type TasksTableContainerProps = {
  showMore: ShowMoreOnMobileState;
  toggleShowMore: (key: ShowMoreOnMobileStateKeys) => () => void;
  tasks: NonNullable<GetShiftReportPdfMetadataQuery['shiftReport']>['tasks'];
  activities: GetShiftReportPdfMetadataQuery['activities'];
};

export const TasksTableContainer = ({
  showMore,
  toggleShowMore,
  tasks,
  activities,
}: TasksTableContainerProps) => {
  const { isMobile } = useMediaType();
  const { t } = useTranslation();

  if (tasks.length === 0) {
    return null;
  }

  return (
    <Flex vertical className="bloc" key="tasks-tables" gap={isMobile ? 8 : 0}>
      {isMobile ? (
        <MobileCardHeader>{t('pages.print.activityNotes')}</MobileCardHeader>
      ) : (
        <h1>{t('pages.print.activityNotes')}</h1>
      )}
      <ShowMoreContainer enableToggle={isMobile} toggleState={showMore.tasks}>
        {activities.reduce<ReactNode[]>((tableList, activity) => {
          const tableData = getTasksByCategory(activity.id, tasks);
          if (!tableData) {
            return tableList;
          }
          return [
            tableList,
            <Table
              key={activity.name}
              scroll={{ x: isMobile ? 'max-content' : undefined }}
              rowKey="key"
              columns={getTableColumnsWithDescription(t)}
              dataSource={tableData}
              bordered
              pagination={false}
              title={() =>
                isMobile ? (
                  <MobileTableHeader>{activity.name}</MobileTableHeader>
                ) : (
                  <h3 className="activity-header">{activity.name}</h3>
                )
              }
              size="small"
            />,
          ];
        }, [])}
      </ShowMoreContainer>

      <ShowMoreToggle
        enableToggle={isMobile}
        toggleState={showMore.tasks}
        toggleFunc={toggleShowMore(SHOW_MORE_ON_MOBILE_STATE_KEYS.TASKS)}
      />
    </Flex>
  );
};
